import { Download, Share2, Smartphone, WifiOff, X } from "lucide-react";
import useAppInstall from "@/hooks/useAppInstall";
import { useSiteLanguage } from "@/contexts/LanguageContext";
import { getPublicUiCopy } from "@/lib/publicPageLocalization";

export default function PWAInstallPrompt({
  embedded = false,
  title,
  description,
  chips,
  showDismiss = true,
  className = "",
}) {
  const { language } = useSiteLanguage();
  const ui = getPublicUiCopy(language, "pwa");
  const { canInstall, dismissed, dismiss, install: triggerInstall, isIOS, isOnline, isStandalone } = useAppInstall();
  const resolvedTitle = title || ui.title;
  const resolvedDescription = description || ui.description;
  const resolvedChips = chips || [ui.chipLaunch, ui.chipMobile, ui.chipSaved];
  const show = embedded
    ? !isStandalone && (canInstall || isIOS)
    : !isStandalone && !dismissed && (canInstall || isIOS);

  const handleInstall = async () => {
    await triggerInstall();
  };

  if (!show) return null;

  return (
    <div
      className={`${
        embedded
          ? "premium-panel app-preview-grid px-5 py-5 text-white shadow-2xl"
          : "fixed bottom-4 left-1/2 z-[60] w-[calc(100%-1.5rem)] max-w-xl -translate-x-1/2 glass rounded-[28px] px-3 py-3 shadow-2xl"
      } ${className}`.trim()}
      data-testid="pwa-install-prompt"
    >
      <div className="flex items-start gap-4">
        <div className={`h-12 w-12 shrink-0 rounded-2xl grid place-items-center shadow-lg ${
          embedded
            ? "bg-white/14 text-white border border-white/15"
            : "bg-[var(--rsi-purple)] text-white shadow-purple-900/20"
        }`}>
          {isOnline ? <Smartphone className="h-5 w-5" /> : <WifiOff className="h-5 w-5" />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className={`text-sm font-semibold ${embedded ? "text-white" : "text-[var(--rsi-navy)]"}`}>
                {resolvedTitle}
              </div>
              <div className={`mt-1 text-xs leading-relaxed ${embedded ? "text-white/72" : "text-gray-600"}`}>
                {resolvedDescription}
              </div>
            </div>
            {showDismiss && !embedded ? (
              <button
                onClick={dismiss}
                className="h-9 w-9 shrink-0 rounded-full grid place-items-center hover:bg-black/5"
                aria-label={ui.dismiss}
                data-testid="pwa-dismiss-button"
              >
                <X className="h-4 w-4" />
              </button>
            ) : null}
          </div>

          <div className="mt-3 flex flex-wrap gap-2 text-[11px]">
            {resolvedChips.map((chip) => (
              <span key={chip} className={embedded ? "app-showcase-chip" : "badge-purple"}>
                {chip}
              </span>
            ))}
          </div>

          {embedded ? (
            <div className="mt-4 grid gap-3 sm:grid-cols-3">
              <div className="metric-tile">
                <div className="text-[11px] uppercase tracking-[0.18em] text-white/55">{ui.launchLabel}</div>
                <div className="mt-1 text-sm font-semibold text-white">{ui.launchTitle}</div>
                <div className="mt-1 text-xs text-white/65">{ui.launchBody}</div>
              </div>
              <div className="metric-tile">
                <div className="text-[11px] uppercase tracking-[0.18em] text-white/55">{ui.experienceLabel}</div>
                <div className="mt-1 text-sm font-semibold text-white">{ui.experienceTitle}</div>
                <div className="mt-1 text-xs text-white/65">{ui.experienceBody}</div>
              </div>
              <div className="metric-tile">
                <div className="text-[11px] uppercase tracking-[0.18em] text-white/55">{ui.accessLabel}</div>
                <div className="mt-1 text-sm font-semibold text-white">{ui.accessTitle}</div>
                <div className="mt-1 text-xs text-white/65">{ui.accessBody}</div>
              </div>
            </div>
          ) : null}

          <div className="mt-4 flex flex-wrap items-center gap-2">
            {canInstall ? (
              <button
                onClick={handleInstall}
                className={`btn-primary !py-2.5 !px-4 text-xs ${
                  embedded ? "!bg-white !text-[var(--rsi-purple)] hover:!bg-white/95" : ""
                }`}
                data-testid="pwa-install-button"
              >
                <Download className="h-4 w-4" /> {ui.installNow}
              </button>
            ) : null}
            {isIOS ? (
              <div className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs ${
                embedded
                  ? "border border-white/12 bg-white/10 text-white/85"
                  : "border border-gray-200 bg-white text-gray-700"
              }`}>
                <Share2 className="h-4 w-4 text-[var(--rsi-purple)]" />
                {ui.iosInstruction}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
